// Pure decision helpers for the patent prior-art search step (Analyze ▾ → Patent Packet).
// Kept separate from AppShell.jsx (a heavy React module with 3Dmol/Firebase/router imports) so
// they're unit-testable without a render harness.

/**
 * Classify a completed prior-art search attempt. `searchFailed` means the network call itself
 * errored or returned non-OK — as opposed to a genuinely successful call that found zero hits.
 * I7b: previously any failure fell through into 'clear' ("no prior art found"), a materially
 * wrong claim for a legal document. Failures now get their own status.
 * H1: `searchStatus` is the service's own coverage verdict ('ok'|'partial'|'unavailable') —
 * a 200 whose every upstream source failed is equivalent to a failed search, NOT "clear".
 */
export function patentSearchStatus({ searchFailed, hits, searchStatus }) {
  if (searchFailed || searchStatus === 'unavailable') return 'search_error';
  return (Array.isArray(hits) && hits.length > 0) ? 'warning' : 'clear';
}

/**
 * Build the prior-art context sentence injected into the Phase-2 LLM prompt / disclosure.
 * When the search failed and the user chose "Proceed anyway", this MUST say the search was
 * unavailable — never assert "no prior art found" (that claim is only true for a genuinely
 * clear, completed search). `sourcesQueried` is only meaningful for a completed search.
 * H1: a partial sweep (`sourcesUnavailable` non-empty) carries an incomplete-coverage caveat.
 */
export function patentPriorArtContext({ status, hits, sourcesQueried, sourcesUnavailable }) {
  if (status === 'search_error') {
    return 'Prior-art search unavailable (the search failed) — no prior-art conclusion could be drawn for this disclosure; proceed with independent legal review before filing.';
  }
  const sourcesStr = (Array.isArray(sourcesQueried) && sourcesQueried.length > 0) ? sourcesQueried.join(', ') : 'patent databases';
  const list = Array.isArray(hits) ? hits : [];
  const base = list.length > 0
    ? `Prior art found (${list.length} hit${list.length !== 1 ? 's' : ''}) across ${sourcesStr}: ${list.map(h => `${h.patent_id} (${h.source})`).join(', ')}.`
    : `No prior art found in ${sourcesStr}.`;
  const missing = Array.isArray(sourcesUnavailable) ? sourcesUnavailable.filter(Boolean) : [];
  if (missing.length === 0) return base;
  return `${base} Coverage incomplete — ${missing.length} source${missing.length !== 1 ? 's' : ''} unavailable (${missing.join(', ')}); prior-art risk may be understated.`;
}
